import { type To } from 'react-router-dom';

/**
 * Appends platform_id to a URL/navigation target if not already present.
 * Handles both string paths and object paths (Partial<Path>).
 *
 * @param to - The navigation target (string or Path object)
 * @param platformId - The platform ID to append
 * @returns The modified navigation target with platform_id included
 */
export const appendPlatformId = (to: To, platformId: string | null): To => {
  if (!platformId) {
    return to;
  }

  // Handle string paths
  if (typeof to === 'string') {
    const url = new URL(to, window.location.origin);

    // Only add platform_id if not already present
    if (!url.searchParams.has('platform_id')) {
      url.searchParams.set('platform_id', platformId);
    }

    // Return pathname + search + hash (without origin for relative paths)
    return `${url.pathname}${url.search}${url.hash}`;
  }

  // Handle object paths (Partial<Path>)
  const { pathname, search, hash } = to;
  const params = new URLSearchParams(search || '');

  // Only add platform_id if not already present
  if (!params.has('platform_id')) {
    params.set('platform_id', platformId);
  }

  return {
    pathname,
    search: params.toString() ? `?${params.toString()}` : '',
    hash,
  };
};
